import React, { useEffect } from 'react';
import setPageMeta from '../../utils/setPageMeta';
import { PAGES } from '../../seo/site';
import CircuitBackground from '../corporate-landing-page/components/CircuitBackground';
import MainframeNavbar from '../corporate-landing-page/components/MainframeNavbar';
import MainframeFooter from '../corporate-landing-page/components/MainframeFooter';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import ConsultationBooking from './components/ConsultationBooking';
import FAQSection from './components/FAQSection';
import LiveChatWidget from './components/LiveChatWidget';
import Icon from '../../components/AppIcon';

// Arranca el circuito de esta página en la sección de agenda (fondo negro)
// -- entra por la izquierda y baja recto todo el borde.
const BookingCircuit = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <div className="absolute left-4 top-0 bottom-0 w-[1.5px] bg-[#8B5CF6] opacity-40" />
  </div>
);

// Recibe el trazo violeta y, al entrar a fondo blanco, vuelve al índigo
// claro. Baja recto por el borde izquierdo y cruza al fondo (left: 38%)
// para entregarle el trazo al Footer en ese mismo punto.
const FAQCircuit = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <div className="absolute left-4 top-0 bottom-0 w-[1.5px] bg-[#C7D2FE] opacity-70" />
    <div className="absolute left-4 bottom-0 w-[calc(38%-16px)] h-[1.5px] bg-[#C7D2FE] opacity-70" />
    <div className="absolute left-[10px] bottom-0 w-[11px] h-[11px] rounded-full bg-[#A5B4FC] opacity-90" />
  </div>
);

const ContactAndConsultationBooking = () => {
  useEffect(() => {
    setPageMeta(PAGES.contact);
    window.scrollTo(0, 0);
  }, []);

  const handleEmergencyContact = () => {
    window.open('tel:+573228571202');
  };

  const handleWhatsAppContact = () => {
    window.open('https://wa.me/+573228571202?text=Hola, necesito información sobre sus servicios de automatización e IA');
  };

  return (
    <div className="min-h-screen bg-white text-black overflow-x-hidden force-light-mode" style={{ fontFamily: 'var(--font-body)' }}>
      <CircuitBackground />
      <MainframeNavbar />

      {/* Hero */}
      <section className="relative z-[1] pt-32 sm:pt-40 pb-16 sm:pb-20 px-5 sm:px-8 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            style={{ fontSize: 'clamp(30px, 5vw, 52px)', lineHeight: 1.15, fontWeight: 500, letterSpacing: '-0.01em' }}
            className="text-black mb-6"
          >
            Contacta con nuestros especialistas
          </h1>
          <p
            className="text-black/60 mb-8 max-w-xl mx-auto"
            style={{ fontSize: 'clamp(15px, 2vw, 18px)', lineHeight: 1.5, fontWeight: 400 }}
          >
            Obtén asesoría personalizada de nuestro equipo. Agenda tu consulta gratuita y descubre
            cómo transformar tu empresa con sistemas autónomos.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
            <button
              type="button"
              onClick={handleWhatsAppContact}
              className="inline-flex items-center gap-2 bg-black text-white text-[14px] font-medium px-6 py-3 rounded-full hover:opacity-80 transition-opacity"
            >
              <Icon name="MessageCircle" size={18} />
              WhatsApp Inmediato
            </button>
            <button
              type="button"
              onClick={handleEmergencyContact}
              className="inline-flex items-center gap-2 text-black text-[14px] font-medium border border-black/20 px-6 py-3 rounded-full hover:bg-black hover:text-white transition-colors duration-200"
            >
              <Icon name="Phone" size={18} />
              Llamada de Soporte
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            <div className="text-center">
              <div className="text-[28px] font-semibold text-black mb-0.5">&lt; 2h</div>
              <div className="text-[12px] font-medium text-black/50 uppercase tracking-wide">Tiempo de Respuesta</div>
            </div>
            <div className="text-center">
              <div className="text-[28px] font-semibold text-black mb-0.5">99%</div>
              <div className="text-[12px] font-medium text-black/50 uppercase tracking-wide">Satisfacción</div>
            </div>
            <div className="text-center">
              <div className="text-[28px] font-semibold text-black mb-0.5">24/7</div>
              <div className="text-[12px] font-medium text-black/50 uppercase tracking-wide">Soporte Continuo</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="relative z-[1] bg-white px-5 sm:px-8 md:px-10 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-6xl mx-auto">
          <ContactForm />
          <ContactInfo />
        </div>
      </section>

      {/* Consultation Booking */}
      <section className="relative z-[1] bg-black px-5 sm:px-8 md:px-10 py-16 sm:py-20 overflow-hidden">
        <BookingCircuit />
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="text-center mb-12 sm:mb-14">
            <h2
              style={{ fontSize: 'clamp(26px, 4.2vw, 40px)', lineHeight: 1.15, fontWeight: 500, letterSpacing: '-0.01em' }}
              className="text-white mb-4"
            >
              Agenda tu consulta gratuita
            </h2>
            <p className="text-white/60 text-[15px] sm:text-[16px] leading-relaxed max-w-xl mx-auto">
              Reserva 60 minutos con nuestros expertos para analizar tus necesidades operativas y
              diseñar un ecosistema de IA a la medida.
            </p>
          </div>
          <ConsultationBooking />
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-[1] bg-white px-5 sm:px-8 md:px-10 py-16 sm:py-20 overflow-hidden">
        <FAQCircuit />
        <div className="relative z-10 max-w-3xl mx-auto">
          <FAQSection />
        </div>
      </section>

      <MainframeFooter />

      <LiveChatWidget />
    </div>
  );
};

export default ContactAndConsultationBooking;
